import { useContext } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../utils/api";
import { ApiError, ApiResponse } from "../types/api";
import { DrawingContext } from "../contexts/DrawingContext";

export interface NotePayload {
  fullText: string;
  dateAdded: string; // ISO date string
  photo?: File | null;
}

export const useAddNote = (projectId: string) => {
  const queryClient = useQueryClient();
  const { coords, setDrawingMode, setDrawingFeatureType } = useContext(DrawingContext);

  return useMutation<ApiResponse<any>, ApiError, NotePayload>({
    mutationFn: (payload) => {
      const form = new FormData();
      form.append("lat", coords?.[1] ?? coords[0][1]); // depending order, assume [lng,lat] order? but front-end uses coords[x,y]? We assume [lng,lat]
      form.append("lng", coords?.[0] ?? coords[0][0]);
      form.append("full_text", payload.fullText);
      form.append("date_added", payload.dateAdded);
      if (payload.photo) {
        form.append("photo", payload.photo);
      }
      return api.request<ApiResponse<any>>(`/api/projects/${projectId}/notes`, {
        method: "POST",
        body: form,
        headers: {},
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "notes"] });
      // Reset drawing state
      setDrawingMode(null);
      setDrawingFeatureType(null);
    },
  });
};

export const useDeleteNote = (projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<any>, ApiError, string | number>({
    mutationFn: (noteId) => {
      return api.request<ApiResponse<any>>(`/api/projects/${projectId}/notes/${noteId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [projectId, "notes"] });
    },
  });
};

export const useNotes = (projectId: string | undefined) => {
  const { data, isLoading, error } = useQuery({
    queryKey: [projectId, "notes"],
    queryFn: () => api.request<ApiResponse<any>>(`/api/projects/${projectId}/notes`),
    enabled: !!projectId,
    staleTime: 0,
  }) as { data: ApiResponse<any>; isLoading: boolean; error: ApiError };

  return { notes: data, isLoading, error };
};
